import map from 'lodash/map';
import size from 'lodash/size';
import times from 'lodash/times';
import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import property from 'lodash/property';

import * as d3 from 'd3';

import React from 'react';
import PropTypes from 'prop-types';

const canvasMargin = {
  top: 15,
  left: 100,
  right: 30,
  bottom: 35
};

const dotsMargin = {
  top: 0,
  right: 0,

  left: 25,
  bottom: 20
};

const colors = {
  primary: {
    light: 'steelblue',
    dark: 'darkblue'
  },

  secondary: {
    light: 'orange',
    dark: '#c15300'
  }
};

class DotsPlotComponent extends React.PureComponent {
  constructor (props) {
    super(props);

    this.drawDotsPlot = () => {
      const dotsWidthMargin = dotsMargin.left + dotsMargin.right;
      const canvasWidthMargin = canvasMargin.left + canvasMargin.right;
      const width = this.props.width - canvasWidthMargin - dotsWidthMargin;

      const dotsHeightMargin = dotsMargin.top + dotsMargin.bottom;
      const canvasHeightMargin = canvasMargin.top + canvasMargin.bottom;
      const height = this.props.height - canvasHeightMargin - dotsHeightMargin;

      const { dots } = this.props;

      const values = map(dots, property('value'));

      let [xMin, xMax] = d3.extent(values);

      if (xMin === xMax) {
        xMin = 0.9 * values[0];
        xMax = 1.1 * values[0];
      }
      const root = d3.select(this.svg);

      root.select('#container').remove();
      root.select('#x-axis').remove();
      root.select('#y-axis').remove();

      const container = root
        .append('g')
        .attr('id', 'container')
        .attr('viewBox', [0, 0, width, height])
        .attr(
          'transform',
          `
          translate(
            ${canvasMargin.left + dotsMargin.left},
            ${canvasMargin.top}
          )
        `
        );

      const radius = 5;
      const diameter = radius * 2;

      const nbins = 150;
      const step = (xMax - xMin) / nbins;

      const bins = times(nbins + 1, binIndex => {
        const rangeStart = xMin + (step * binIndex);
        const rangeFinish = xMin + (step * (binIndex + 1));

        const sortedDots = sortBy(
          filter(
            dots,
            dot => dot.value >= rangeStart && dot.value < rangeFinish
          ),
          'value'
        );

        const averageValueForColumn =
          sortedDots.length >= 1
            ? sortedDots.reduce((a, b) => ({ value: a.value + b.value }), {
              value: 0
            }).value / sortedDots.length
            : 0;

        const chunk = map(
          sortedDots.map(dot => ({ ...dot, value: averageValueForColumn })),
          (dot, dotIndex) => {
            const fill =
              dot.value <= this.props.leftCutoff ||
              dot.value >= this.props.rightCutoff
                ? colors.secondary
                : colors.primary;

            return {
              ...dot,
              height: dotIndex,

              fill:
                dot.id === this.props.selectedDotId ? fill.dark : fill.light
            };
          }
        );

        chunk.x0 = rangeStart;
        chunk.x1 = rangeFinish;

        return chunk;
      });

      const maxAmountOfCirclesInColumn = Math.max(...map(bins, size));
      const maxBinHeight = maxAmountOfCirclesInColumn + (maxAmountOfCirclesInColumn * step);
      const minRecommendedHeight = Math.round(height / diameter);

      const yMax =
        maxBinHeight < minRecommendedHeight
          ? minRecommendedHeight
          : maxBinHeight;

      const xScale = d3
        .scaleLinear()
        .domain([xMin, xMax])
        .rangeRound([0, width]);

      const yScale = d3.scaleLinear().domain([0, yMax]).rangeRound([height, 0]);

      const xAxisContainer = root
        .append('g')
        .attr('id', 'x-axis')
        .attr('class', 'dotplot')
        .attr(
          'transform',
          `
          translate(
            ${canvasMargin.left + dotsMargin.left},
            ${height + canvasMargin.top + dotsMargin.bottom}
          )
        `
        );

      const yAxisContainer = root
        .append('g')
        .attr('id', 'y-axis')
        .attr('class', 'dotplot')
        .attr(
          'transform',
          `
          translate(
            ${canvasMargin.left},
            ${canvasMargin.top}
          )
        `
        );

      // Improved ticks for graph
      let xTicks = xScale.ticks();
      const tickStep = Math.abs(xTicks[1] - xTicks[0]) / 2;

      if (Math.abs(xMin - xTicks[0]) < tickStep) {
        xTicks[0] = xMin;
      } else {
        xTicks = [xMin, ...xTicks];
      }

      if (Math.abs(xMax - xTicks[xTicks.length - 1]) < tickStep) {
        xTicks[xTicks.length - 1] = xMax;
      } else {
        xTicks = [...xTicks, xMax];
      }

      const axisBottom = d3
        .axisBottom(xScale)
        .tickValues(xTicks)
        .tickFormat(value => {
          return Number(value.toFixed(3)).toString();
        });

      xAxisContainer.append('g').attr('id', 'axisX').style('font-size', '14px').call(axisBottom);

      const axisLeft = d3.axisLeft(yScale);

      yAxisContainer.append('g').style('font-size', '14px').call(axisLeft);

      if (this.props.xAxisLabel) {
        xAxisContainer
          .append('text')
          .text(this.props.xAxisLabel)
          .attr('class', 'x-axis')
          .attr('dy', '-5')
          .attr('dx', width)
          .attr('font-size', '16')
          .attr('text-anchor', 'end');
      }

      if (this.props.yAxisLabel) {
        yAxisContainer
          .append('text')
          .text(this.props.yAxisLabel)
          .attr('class', 'y-axis')
          .attr('dy', '15')
          .attr('font-size', '16')
          .attr('text-anchor', 'end')
          .attr('transform', 'rotate(-90)');
      }

      const binsContainer = container
        .selectAll('g')
        .data(bins)
        .enter()
        .append('g');

      const circles = binsContainer.selectAll('circle').data(d => d);

      circles
        .enter()
        .append('circle')
        .attr('cx', d => xScale(d.value))
        .attr('cy', d => yScale(d.height))
        .attr('r', radius)
        .attr('fill', d => d.fill)
        .on('click', this.props.onDotClick);

      let simulatedPLineContainer = null;

      if (this.props.simulatedPLineX !== null) {
        simulatedPLineContainer = container.append('g').attr(
          'transform',
          `
            translate(${xScale(this.props.simulatedPLineX)}, 0)
          `
        );

        simulatedPLineContainer
          .append('path')
          .data([[0, yMax]])
          .attr('stroke', 'darkorange')
          .attr(
            'd',
            d3
              .line()
              .x(() => 0)
              .y(yScale)
          );

        if (this.props.simulatedPLineLabel) {
          simulatedPLineContainer
            .append('text')
            .attr('dy', '12')
            .attr('font-size', '12')
            .attr('text-anchor', 'end')
            .attr('transform', 'rotate(-90)')
            .text(this.props.simulatedPLineLabel);
        }
      }

      const zoomed = ({ transform }) => {
        const newXScale = transform.rescaleX(xScale);

        d3.select('#axisX').call(d3.axisBottom(newXScale));

        root.select('#container')
          .selectAll('circle')
          .attr('display', d => newXScale(d.value) < 0 || newXScale(d.value) > width ? 'none' : '')
          .attr('cx', d => newXScale(d.value));
      };

      const zoom = d3.zoom()
        .scaleExtent([0.5, 10])
        .extent([[canvasMargin.left + dotsMargin.left, 50], [width - canvasMargin.right, height]])
        .translateExtent([[canvasMargin.left + dotsMargin.left, -Infinity], [width - canvasMargin.right, Infinity]])
        .on('zoom', zoomed);

      root.call(zoom).call(zoom.transform, d3.zoomIdentity);
    };

    this.componentDidMount = this.drawDotsPlot;
    this.componentDidUpdate = this.drawDotsPlot;

    this.saveSvgNode = svg => {
      this.svg = svg;
    };
  }

  render () {
    return (
      <svg
        ref={this.saveSvgNode}
        width={this.props.width}
        height={this.props.height}
      />
    );
  }
}

DotsPlotComponent.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,

  dots: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      value: PropTypes.number.isRequired
    }).isRequired
  ).isRequired,

  leftCutoff: PropTypes.number,
  rightCutoff: PropTypes.number,

  onDotClick: PropTypes.func,
  selectedDotId: PropTypes.number,

  simulatedPLineX: PropTypes.number,
  simulatedPLineLabel: PropTypes.string,

  yAxisLabel: PropTypes.string,
  xAxisLabel: PropTypes.string
};

DotsPlotComponent.defaultProps = {
  simulatedPLineLabel: null,
  simulatedPLineX: null,

  selectedDotId: null,
  onDotClick: null,

  xAxisLabel: null,
  yAxisLabel: null,

  leftCutoff: -Infinity,
  rightCutoff: +Infinity
};

export default DotsPlotComponent;
